import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Avatar,
  Divider,
  IconButton,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Celebration as CelebrationIcon,
  LocalPlay as ToysIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { divertilandiaColors } from '../theme/divertilandiaColors';
import { dashboardService } from '../services/api';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardService.getStats();
      setStats(data);
    } catch (err: any) {
      setError('Error al cargar las estadísticas del dashboard');
      console.error('Error fetching dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} sx={{ color: divertilandiaColors.standard.primary }} />
        <Typography variant="h6" sx={{ ml: 2, color: divertilandiaColors.standard.primary }}>
          Cargando estadísticas...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchStats}
          sx={{ backgroundColor: divertilandiaColors.standard.primary }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No hay datos disponibles
        </Alert>
      </Box>
    );
  }

  const statCards = [
    {
      title: 'Eventos Totales',
      value: stats.totalEvents || 0,
      icon: <EventIcon sx={{ fontSize: 40 }} />,
      color: divertilandiaColors.standard.primary,
      bgColor: `${divertilandiaColors.standard.primary}15`,
      change: `${stats.totalEvents || 0} eventos`,
      changeColor: 'info.main'
    },
    {
      title: 'Eventos Pendientes',
      value: stats.pendingEvents || 0,
      icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
      color: divertilandiaColors.rainbow.orange,
      bgColor: `${divertilandiaColors.rainbow.orange}15`,
      change: 'Por confirmar',
      changeColor: 'warning.main'
    },
    {
      title: 'Eventos Confirmados',
      value: stats.confirmedEvents || 0,
      icon: <CheckIcon sx={{ fontSize: 40 }} />,
      color: divertilandiaColors.rainbow.green,
      bgColor: `${divertilandiaColors.rainbow.green}15`,
      change: 'Confirmados',
      changeColor: 'success.main'
    },
    {
      title: 'Ingresos del Mes',
      value: `$${(stats.monthlyRevenue || 0).toLocaleString()}`,
      icon: <MoneyIcon sx={{ fontSize: 40 }} />,
      color: divertilandiaColors.rainbow.violet,
      bgColor: `${divertilandiaColors.rainbow.violet}15`,
      change: stats.currentMonth || 'Este mes',
      changeColor: 'success.main'
    },
    {
      title: 'Juegos Disponibles',
      value: stats.availableGames || 0,
      icon: <ToysIcon sx={{ fontSize: 40 }} />,
      color: divertilandiaColors.rainbow.blue,
      bgColor: `${divertilandiaColors.rainbow.blue}15`,
      change: `${stats.totalGames || 0} total`,
      changeColor: 'info.main'
    },
    {
      title: 'Clientes Únicos',
      value: stats.uniqueClients || 0,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: divertilandiaColors.rainbow.red,
      bgColor: `${divertilandiaColors.rainbow.red}15`,
      change: 'Familias',
      changeColor: 'success.main'
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confirmado';
      case 'pending': return 'Pendiente';
      case 'completed': return 'Completado';
      default: return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed': return <CheckIcon fontSize="small" />;
      case 'pending': return <ScheduleIcon fontSize="small" />;
      case 'completed': return <CelebrationIcon fontSize="small" />;
      default: return <EventIcon fontSize="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header con bienvenida */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h3" sx={{ 
              fontWeight: 'bold', 
              color: divertilandiaColors.standard.primary,
              mb: 1
            }}>
              ¡Bienvenido a Divertilandia! 🎉
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {currentTime.toLocaleDateString('es-ES', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })} - {currentTime.toLocaleTimeString('es-ES', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </Typography>
          </Box>
          <IconButton 
            size="large" 
            onClick={fetchStats}
            sx={{ 
              backgroundColor: divertilandiaColors.standard.primary,
              color: 'white',
              '&:hover': { backgroundColor: divertilandiaColors.rainbow.blue }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
        
        <Paper sx={{ 
          p: 2, 
            background: `linear-gradient(135deg, ${divertilandiaColors.rainbow.red}20, ${divertilandiaColors.rainbow.blue}20)`,
          border: `2px solid ${divertilandiaColors.standard.primary}30`
        }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ 
                bgcolor: divertilandiaColors.standard.primary, 
                width: 56, 
                height: 56 
              }}>
                <StarIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: divertilandiaColors.standard.primary }}>
                  ¡Bienvenido a tu Panel de Control! 🎉
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Gestiona tu negocio con {stats.uniqueClients || 0} clientes satisfechos
                </Typography>
              </Box>
            </Box>
        </Paper>
      </Box>

      {/* Estadísticas principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card sx={{ 
              height: '100%', 
              textAlign: 'center',
              background: card.bgColor,
              border: `2px solid ${card.color}30`,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 25px ${card.color}40`
              }
            }}>
              <CardContent>
                <Box sx={{ mb: 2, color: card.color }}>
                  {card.icon}
                </Box>
                <Typography variant="h4" component="div" sx={{ 
                  fontWeight: 'bold', 
                  mb: 1,
                  color: card.color
                }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {card.title}
                </Typography>
                <Chip 
                  label={card.change}
                  size="small"
                  sx={{ 
                    backgroundColor: card.changeColor,
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Eventos Recientes */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ 
            p: 3,
            background: `linear-gradient(135deg, ${divertilandiaColors.rainbow.green}10, ${divertilandiaColors.rainbow.blue}10)`
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold',
                color: divertilandiaColors.standard.primary
              }}>
                📅 Próximos Eventos
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<CalendarIcon />}
                onClick={() => navigate('/calendar')}
                sx={{ borderColor: divertilandiaColors.standard.primary }}
              >
                Ver Todos
              </Button>
            </Box>
            <List>
              {(stats.upcomingEvents || []).map((event: any, index: number) => (
                <React.Fragment key={event.id}>
                  <ListItem sx={{ 
                    borderRadius: 2,
                    mb: 1,
                    backgroundColor: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: getStatusColor(event.status) === 'success' ? divertilandiaColors.rainbow.green :
                                getStatusColor(event.status) === 'warning' ? divertilandiaColors.rainbow.orange :
                                divertilandiaColors.rainbow.blue
                      }}>
                        {getStatusIcon(event.status)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {event.client}
                          </Typography>
                          <Typography variant="h6" sx={{ 
                            color: divertilandiaColors.rainbow.violet,
                            fontWeight: 'bold'
                          }}>
                            ${event.amount.toLocaleString()}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            👶 {event.child} • 📦 {event.package}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            📅 {event.date} a las {event.time}
                          </Typography>
                          <Box mt={1}>
                            <Chip
                              label={getStatusText(event.status)}
                              size="small"
                              color={getStatusColor(event.status) as any}
                              icon={getStatusIcon(event.status)}
                            />
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < (stats.upcomingEvents || []).length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Paquetes Más Populares */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ 
            p: 3,
            background: `linear-gradient(135deg, ${divertilandiaColors.rainbow.violet}10, ${divertilandiaColors.rainbow.red}10)`
          }}>
            <Typography variant="h5" gutterBottom sx={{ 
              fontWeight: 'bold',
              color: divertilandiaColors.standard.primary,
              mb: 3
            }}>
              🏆 Paquetes Populares
            </Typography>
            
            {(stats.popularPackages || []).map((pkg: any, index: number) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {pkg.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {pkg.bookings} reservas
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={pkg.percentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: `${divertilandiaColors.standard.primary}20`,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: index === 0 ? divertilandiaColors.rainbow.green :
                                     index === 1 ? divertilandiaColors.rainbow.blue :
                                     index === 2 ? divertilandiaColors.rainbow.orange :
                                     divertilandiaColors.rainbow.violet
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {pkg.percentage}% de popularidad
                </Typography>
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />
            
            {/* Resumen de Ingresos */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ 
                color: divertilandiaColors.standard.primary,
                fontWeight: 'bold',
                mb: 1
              }}>
                💰 Resumen Financiero
              </Typography>
              <Typography variant="h4" sx={{ 
                color: divertilandiaColors.rainbow.green,
                fontWeight: 'bold',
                mb: 1
              }}>
                ${(stats.totalRevenue || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ingresos totales acumulados
              </Typography>
              
              {stats.monthlyProfit !== undefined && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ 
                    color: stats.monthlyProfit >= 0 ? divertilandiaColors.rainbow.green : divertilandiaColors.rainbow.red,
                    fontWeight: 'bold'
                  }}>
                    Utilidad del mes: ${(stats.monthlyProfit || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gastos del mes: ${(stats.monthlyExpenses || 0).toLocaleString()}
                  </Typography>
                </Box>
              )}
              
              <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                <TrendingUpIcon sx={{ color: divertilandiaColors.rainbow.green }} />
                <Typography variant="body1" sx={{ 
                  color: divertilandiaColors.rainbow.green,
                  fontWeight: 'bold'
                }}>
                  {stats.totalEvents || 0} eventos realizados
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Footer motivacional */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Paper sx={{ 
          p: 3,
          background: `linear-gradient(135deg, ${divertilandiaColors.rainbow.yellow}20, ${divertilandiaColors.rainbow.orange}20)`,
          border: `2px solid ${divertilandiaColors.standard.primary}30`
        }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 'bold',
            color: divertilandiaColors.standard.primary,
            mb: 1
          }}>
            🎈 ¡Creando sonrisas desde 2020! 🎈
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Más de {stats.totalEvents || 0} eventos realizados • {stats.uniqueClients || 0} familias felices • {stats.availableGames || 0} juegos increíbles
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;